import React, { ChangeEvent, CSSProperties, useEffect, useState } from 'react'
import { cx } from '@emotion/css'
import { ErrorOutline, RotateRight } from '@mui/icons-material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  Box,
  BoxProps,
  IconButton,
  Slider as MuiSlider,
  SliderProps,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import AvatarEditor, { Position } from 'react-avatar-editor'
import { Buffer } from 'buffer'
import { Avatar } from '../Avatar'
import { Button } from '../Button'
import { getClassName } from '../../utils'

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp']
const OVERLAY_BORDER_STYLE = 'dashed'
const OVERLAY_BORDER_WIDTH = 2
const OVERLAY_OFFSET = 2
const SPACING_HORIZONTAL = 92
const SPACING_VERTICAL = 40
const DEFAULT_WIDTH = 256

export interface AvatarMetadata {
  x?: number
  y?: number
  zoomLevel?: number
  rotationAngle?: number
  width?: number
  height?: number
}

export type AvatarVariant = 'circular' | 'rounded' | 'square'

type FileName = {
  name: string
  extension: string
}

type Spacing = {
  horizontal?: number
  vertical?: number
}

export interface IAvatarPicker {
  maxZoom?: number
  minZoom?: number
  onSave?: (
    originalFile: File | null,
    editedFile: File | null,
    metadata: AvatarMetadata
  ) => Promise<void>
  onCancel?: () => void
  src?: string
  text?: string
  variant?: AvatarVariant
  width?: number
  spacing?: Spacing
  metaData?: AvatarMetadata
  wrapperStyle?: CSSProperties | string
  topButtonBarStyle?: CSSProperties | string
  bottomButtonBarStyle?: CSSProperties | string
  deleteButtonStyle?: CSSProperties | string
  uploadButtonStyle?: CSSProperties | string
  cancelButtonStyle?: CSSProperties | string
  saveButtonStyle?: CSSProperties | string
  zoomSliderStyle?: CSSProperties | string
  straightenSliderStyle?: CSSProperties | string
  rotateButtonStyle?: CSSProperties | string
}

const Wrapper = styled(Box, {
  name: 'Wrapper',
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'spacing',
})<BoxProps & { width?: number; spacing?: Spacing }>(
  ({ theme, width = DEFAULT_WIDTH, spacing }) => {
    const spacingHorizontal = spacing?.horizontal ?? SPACING_HORIZONTAL
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: theme.palette.dark.main,
      gap: 1,
      width: `${width + 2 * spacingHorizontal}px`,
      padding: theme.spacing(3, 4),
    }
  }
)

const DropBox = styled(
  (
    props: BoxProps & {
      variant?: AvatarVariant
      dragActive?: boolean
      width?: number
      spacing?: Spacing
      editMode?: boolean
    }
  ) => <Box {...props} />,
  {
    name: 'DropBox',
    shouldForwardProp: (prop) =>
      prop !== 'dragActive' &&
      prop !== 'variant' &&
      prop !== 'width' &&
      prop !== 'spacing' &&
      prop !== 'editMode',
  }
)(
  ({
    theme,
    variant,
    dragActive,
    editMode,
    width = DEFAULT_WIDTH,
    spacing,
  }) => {
    const spacingHorizontal = spacing?.horizontal ?? SPACING_HORIZONTAL
    const spacingVertical = spacing?.vertical ?? SPACING_VERTICAL
    return {
      boxSizing: 'border-box',
      borderRadius:
        variant === 'circular' ? '50%' : variant === 'rounded' ? '4px' : 0,
      border: `${OVERLAY_BORDER_WIDTH}px ${OVERLAY_BORDER_STYLE}`,
      borderColor: dragActive
        ? theme.palette.primary.main
        : theme.palette.background.paper,
      width: `${width - 2 * (OVERLAY_OFFSET + OVERLAY_BORDER_WIDTH)}px`,
      height: `${width - 2 * (OVERLAY_OFFSET + OVERLAY_BORDER_WIDTH)}px`,
      maxHeight: `${width - 2 * (OVERLAY_OFFSET + OVERLAY_BORDER_WIDTH)}px`,
      position: 'relative',
      top: `-${
        width -
        OVERLAY_OFFSET -
        OVERLAY_BORDER_WIDTH +
        (editMode ? spacingVertical + 3 : 0)
      }px`,
      left: `${
        OVERLAY_OFFSET +
        OVERLAY_BORDER_WIDTH +
        (editMode ? spacingHorizontal : 0)
      }px`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: `rgba(255,255,255,0.8)`,
      pointerEvents: 'none',
    }
  }
)

const Slider: React.FC<SliderProps & { title?: string }> = ({
  title,
  ...props
}) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        width: '100px',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: theme.spacing(0, 1),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: props.disabled
              ? theme.palette.white.main
              : theme.palette.white.inactive,
            fontSize: theme.typography.pxToRem(12),
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: props.disabled
              ? theme.palette.white.main
              : theme.palette.white.inactive,
            fontSize: theme.typography.pxToRem(12),
          }}
        >
          {props.value}
        </Typography>
      </Box>
      <MuiSlider
        {...props}
        classes={{
          rail: !props.disabled
            ? cx(
                getClassName({
                  backgroundColor: theme.palette.primary[100],
                  opacity: 1,
                })
              )
            : undefined,
        }}
      />
    </Box>
  )
}

export const AvatarPicker = React.forwardRef<HTMLDivElement, IAvatarPicker>(
  (
    {
      maxZoom = 4,
      minZoom = 1,
      onCancel,
      onSave,
      src,
      text,
      variant = 'circular',
      width = DEFAULT_WIDTH,
      spacing,
      metaData = {
        x: 0.5,
        y: 0.5,
        zoomLevel: 1,
        rotationAngle: 0,
      },
      wrapperStyle,
      topButtonBarStyle,
      deleteButtonStyle,
      uploadButtonStyle,
      bottomButtonBarStyle,
      cancelButtonStyle,
      saveButtonStyle,
      zoomSliderStyle,
      straightenSliderStyle,
      rotateButtonStyle,
    },
    ref
  ) => {
    const theme = useTheme()

    const [imgSrc, setImgSrc] = useState(src || '')
    const [file, setFile] = useState<File | null>(null)
    const [fileName, setFileName] = useState('')
    const [zoom, setZoom] = useState(1)
    const [baseRotation, setBaseRotation] = useState(0)
    const [rotation, setRotation] = useState(0)
    const [editMode, setEditMode] = useState(!!src)
    const [dragActive, setDragActive] = useState(false)
    const [error, setError] = useState('')
    const [position, setPosition] = useState<Position>({ x: 0.5, y: 0.5 })
    const [deleted, setDeleted] = useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const editorRef = React.useRef<AvatarEditor>(null)

    const spacingHorizontal = spacing?.horizontal ?? SPACING_HORIZONTAL
    const spacingVertical = spacing?.vertical ?? SPACING_VERTICAL

    useEffect(() => {
      const { rotationAngle = 0, zoomLevel = 1, x = 0.5, y = 0.5 } = metaData

      const sign = Math.sign(rotationAngle)
      const a = Math.abs(rotationAngle)
      const angle =
        a % 45 === 0
          ? a % 90
          : Math.floor(a / 45) % 2 === 0
          ? a % 45
          : (a % 45) - 45
      const baseRot = a - angle

      setBaseRotation(sign * baseRot)
      setRotation(sign * angle)
      setZoom(zoomLevel)
      setPosition({ x, y })
    }, [])

    useEffect(() => {
      if (!file) {
        setImgSrc('')
        setEditMode(false)
        return
      }

      const url = URL.createObjectURL(file)
      setImgSrc(url)
      setFileName(file.name)
      setEditMode(true)
      setDeleted(false)

      return () => URL.revokeObjectURL(url)
    }, [file])

    useEffect(() => {
      if (!src) {
        setImgSrc('')
        setEditMode(false)
        return
      }

      setImgSrc(src)
      setFileName(src.split('/').reverse()[0])
      setEditMode(true)
      setDeleted(false)
    }, [src])

    const handleDrag = (e: any) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true)
      } else if (e.type === 'dragleave') {
        setDragActive(false)
      }
    }

    const handleDrop = (e: any) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      if (e.dataTransfer?.files && e.dataTransfer?.files[0]) {
        handleFileInput(e.dataTransfer?.files[0])
      }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      if (e.target.files && e.target.files[0]) {
        handleFileInput(e.target.files[0])
      }
    }

    const deleteButtonClick = () => {
      setImgSrc('')
      setFile(null)
      setDeleted(true)
    }

    const uploadButtonClick = () => {
      if (inputRef?.current) {
        inputRef.current.click()
      }
    }

    const getFileNameParams = (_fileName: string): FileName => {
      const fileNameParams = _fileName.split('.')
      const extension =
        fileNameParams.length > 1
          ? fileNameParams[fileNameParams.length - 1].toLowerCase()
          : ''
      return {
        name: fileNameParams[0],
        extension,
      }
    }

    const handleFileInput = (droppedFile: File) => {
      const fileName = droppedFile?.name ?? ''

      if (droppedFile && fileName) {
        const fileNameParams = getFileNameParams(fileName)
        if (IMAGE_TYPES.includes(fileNameParams.extension)) {
          setFile(droppedFile)
          setBaseRotation(0)
          setRotation(0)
          setZoom(1)
          setPosition({ x: 0.5, y: 0.5 })
        } else {
          setError('Dropped file not supported')
        }
      }
    }

    const handleZoom = (_: Event, newValue: number | number[]) => {
      setZoom(Array.isArray(newValue) ? newValue[0] : newValue)
    }

    const handleRotation = (_: Event, newValue: number | number[]) => {
      setRotation(Array.isArray(newValue) ? newValue[0] : newValue)
    }

    const handlePosition = (pos: Position) => {
      setPosition(pos)
    }

    const dataURLtoFile = (dataUrl: string, filename: string) => {
      const arr = dataUrl.split(',')
      const mime = arr[0].match(/:(.*?);/)?.[1]
      const bstr = Buffer.from(arr[1], 'base64')
      const u8arr = new Uint8Array(bstr.length)
      let n = bstr.length
      while (n--) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        u8arr[n] = bstr.at(n)!
      }

      return new File([u8arr], filename, { type: mime })
    }

    const handleSave = async () => {
      const metadata = {
        zoomLevel: zoom,
        rotationAngle: (baseRotation + rotation) % 360,
        ...position,
      }
      if (editorRef?.current) {
        const dataURL = editorRef.current.getImageScaledToCanvas().toDataURL()
        const editedFile = dataURLtoFile(dataURL, fileName)
        await onSave?.(file, editedFile, metadata)
      } else {
        await onSave?.(file, null, metadata)
      }
    }

    return (
      <Wrapper
        ref={ref}
        width={width}
        spacing={spacing}
        className={getClassName(wrapperStyle)}
      >
        <Typography
          variant="h2"
          sx={{
            color: theme.palette.white.primary,
            fontSize: theme.typography.pxToRem(24),
            lineHeight: '36px',
            fontWeight: 500,
            alignSelf: 'start',
            marginBottom: theme.spacing(3.5),
          }}
        >
          Avatar editor
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: theme.spacing(1),
          }}
          className={getClassName(topButtonBarStyle)}
        >
          <Button
            type="button"
            variant="outlined"
            color="white"
            size="tiny"
            startIcon="file_upload"
            style={uploadButtonStyle ?? { fontWeight: 400, letterSpacing: 0 }}
            onClick={uploadButtonClick}
            text="Upload"
          />
          <Button
            type="button"
            variant="outlined"
            color="white"
            size="tiny"
            startIcon="delete"
            style={deleteButtonStyle ?? { fontWeight: 400, letterSpacing: 0 }}
            onClick={deleteButtonClick}
            text="Remove"
            disabled={!editMode}
          />
        </Box>
        <input
          ref={inputRef}
          type="file"
          onChange={handleChange}
          style={{ display: 'none' }}
          accept="image/jpeg, image/jpg, image/png"
        />
        <Box
          sx={{
            height: `${width + 2 * spacingVertical}px`,
            display: 'flex',
            alignItems: 'center',
            marginTop: theme.spacing(1),
          }}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={() => setError('')}
        >
          <Box
            sx={{
              height: `${width + (editMode ? 2 * spacingVertical : 0)}px`,
            }}
          >
            {!editMode && (
              <Avatar
                imageSrc={imgSrc}
                size={width}
                variant={variant}
                fontSize={width / 2}
                text={text}
              />
            )}
            {editMode && imgSrc && (
              <AvatarEditor
                ref={editorRef}
                image={imgSrc}
                borderRadius={
                  variant === 'square'
                    ? 0
                    : variant === 'rounded'
                    ? 4
                    : width / 2
                }
                scale={zoom}
                rotate={baseRotation + rotation}
                position={position}
                onPositionChange={handlePosition}
                color={[0, 0, 0, 0.54]}
                border={[spacingHorizontal, spacingVertical]}
                crossOrigin="anonymous"
                width={width}
                height={width}
              />
            )}
            {(dragActive || error) && (
              <DropBox
                variant={variant}
                dragActive={dragActive}
                width={width}
                spacing={spacing}
                editMode={editMode}
              >
                <Box
                  sx={{
                    width: '80%',
                    margin: 'auto',
                    textAlign: 'center',
                    fontSize: '1rem',
                    color: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {!error && (
                    <>
                      <CloudUploadIcon sx={{ fontSize: '3rem' }} />
                      <Typography variant="body2">
                        Drop files to instantly upload them
                      </Typography>
                    </>
                  )}
                  {error && (
                    <>
                      <ErrorOutline sx={{ fontSize: '3rem' }} />
                      <Typography variant="body2">{error}</Typography>
                    </>
                  )}
                </Box>
              </DropBox>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: theme.spacing(2),
            width: '100%',
            mt: 2,
          }}
        >
          <Slider
            title="Zoom"
            min={minZoom}
            max={maxZoom}
            step={0.1}
            value={zoom}
            onChange={handleZoom}
            disabled={!editMode}
            className={getClassName(zoomSliderStyle)}
          />
          <Slider
            title="Straighten"
            min={-45}
            max={45}
            value={rotation}
            onChange={handleRotation}
            disabled={!editMode}
            onKeyPress={(event) => {
              if (event.key === '0') {
                setRotation(0)
              }
            }}
            className={getClassName(straightenSliderStyle)}
          />
          <IconButton
            color="primary"
            disabled={!editMode}
            onClick={() => {
              setBaseRotation((prev) => (prev + 90) % 360)
            }}
            className={getClassName(rotateButtonStyle)}
            sx={{
              [`&.Mui-disabled`]: {
                color: theme.palette.black.disabled,
              },
              marginBottom: theme.spacing(-0.5),
            }}
          >
            <RotateRight />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: theme.spacing(2),
            marginTop: theme.spacing(6),
          }}
          className={getClassName(bottomButtonBarStyle)}
        >
          <Button
            type="button"
            variant="contained"
            color={!imgSrc && !deleted ? 'white' : 'primary'}
            onClick={handleSave}
            disabled={!imgSrc && !deleted}
            text="Save"
            style={saveButtonStyle}
          />
          <Button
            type="button"
            variant="outlined"
            color="white"
            onClick={() => onCancel?.()}
            text="Cancel"
            style={cancelButtonStyle}
          />
        </Box>
      </Wrapper>
    )
  }
)

AvatarPicker.displayName = 'AvatarPicker'
